import { HomeOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import React, { useState, useEffect } from 'react';
import { useParams, NavLink, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import FeatherIcon from 'feather-icons-react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Breadcrumb } from 'antd';
import PasswordVaultList from '../../../components/password-vault/PasswordVaultList';
import PasswordVaultForm from '../../../components/password-vault/PasswordVaultForm';
import { PageHeader } from '../../../components/page-headers/page-headers';
import { Button } from '../../../components/buttons/buttons';
import { Cards } from '../../../components/cards/frame/cards-frame';
import { BreadcrumbWrapperStyle } from '../../../components/ui-elements/ui-elements-styled';
import { alertModal, Modal } from '../../../components/modals/antd-modals';
import { notificationError, notificationSuccess } from '../../../components/utilities/notification';
import {
  QUERY_BOT_VAULT_CREATE,
  QUERY_BOT_VAULT_FIND_ALL,
  QUERY_BOT_VAULT_REMOVE,
  QUERY_BOT_VAULT_UPDATE,
} from '../../../queries';
import { UserPermissions } from '../../../utils/enums/permissions.enum';
import { Main } from '../styled';

const BotVaultManagement = () => {
  const ChangeLayoutMode = useSelector(state => state.changeLayoutMode.data);
  const darkMode = ChangeLayoutMode;
  const { t } = useTranslation();
  const { botId } = useParams();
  const history = useHistory();
  const [showModal, setShowModal] = useState(false);
  const [stateVariables, setStateVariables] = useState([]);
  const [stateVaultId, setStateVaultId] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const currentLoading = useSelector(state => state.currentUserTenant.loading);
  const currentTenant = useSelector(state => state.currentUserTenant.currentTenant);
  const currentUser = useSelector(state => state.currentUserTenant.currentUser);
  const initialModalInfo = {
    action: 'CREATE',
    form: {
      name: '',
      value: '',
      description: '',
      user: 'SHARED',
      isTenantVariable: false,
    },
  };
  const [modalInfo, setModalInfo] = useState(initialModalInfo);
  const queryArgs = {
    variables: {
      id: botId,
    },
    fetchPolicy: 'network-only',
  };
  const [botVaultCreate] = useMutation(QUERY_BOT_VAULT_CREATE);
  const [botVaultUpdate] = useMutation(QUERY_BOT_VAULT_UPDATE);
  const [botVaultRemove] = useMutation(QUERY_BOT_VAULT_REMOVE);
  const { loading: vaultLoading, data: vaultData, refetch: vaultRefetch } = useQuery(
    QUERY_BOT_VAULT_FIND_ALL,
    queryArgs,
  );

  const handleUpdateView = () => {
    vaultRefetch();
    setShowModal(false);
    setModalInfo(initialModalInfo);
  };

  const handleModalStatus = () => {
    setModalInfo(initialModalInfo);
    setShowModal(!showModal);
  };

  const handleSecureVariableUpdate = secureVariable => {
    setModalInfo({
      action: 'UPDATE',
      form: {
        ...secureVariable,
      },
    });
    setShowModal(!showModal);
  };

  const secureVariableCreate = payload => {
    setIsSubmitted(true);
    botVaultCreate({
      variables: { input: payload },
    })
      .then(({ data }) => {
        const { success, code: responseCode } = data.passwordVaultCreate;
        if (success) {
          notificationSuccess(t('botVault.successAssigned'));
          handleUpdateView();
        } else notificationError(t(`codeResponse.${responseCode}`));
      })
      .catch(e => {
        // eslint-disable-next-line no-console
        console.log('Bot Vault Create Error ', e);
        notificationError(t('codeResponse.UNEXPECTED_ERROR'));
      })
      .finally(() => {
        setIsSubmitted(false);
      });
  };

  const secureVariableUpdate = payload => {
    setIsSubmitted(true);
    botVaultUpdate({
      variables: { input: payload },
    })
      .then(({ data }) => {
        const { success, code: responseCode } = data.passwordVaultUpdate;
        if (success) {
          notificationSuccess(t('botVault.successUpdated'));
          handleUpdateView();
        } else notificationError(t(`codeResponse.${responseCode}`));
      })
      .catch(e => {
        // eslint-disable-next-line no-console
        console.log('Bot Vault Update Error ', e);
        notificationError(t('codeResponse.UNEXPECTED_ERROR'));
      })
      .finally(() => {
        setIsSubmitted(false);
      });
  };

  const handleSecureVariableDestroy = (oldName, user) => {
    alertModal.confirm({
      title: t('botVault.confirmRemove'),
      onOk() {
        botVaultRemove({
          variables: {
            input: {
              _id: stateVaultId,
              oldName,
              user
            },
          },
        })
          .then(({ data }) => {
            const { success, code } = data.passwordVaultRemove;

            if (success) {
              setStateVariables(prevState => prevState.filter(item => item.name !== oldName));
              notificationSuccess(t('botVault.successDeleted'));
            } else {
              notificationError(t(`codeResponse.${code}`));
            }
          })
          .catch(e => {
            // eslint-disable-next-line no-console
            console.log('Bot Vault Delete Error ', e);
            notificationError(t('codeResponse.UNEXPECTED_ERROR'));
          })
          .finally(() => handleUpdateView());
      },
      okText: t('general.confirm'),
    });
  };

  const handleSubmit = formValues => {
    const payload = {
      name: formValues.name,
      value: formValues.value,
      description: formValues.description,
      user: formValues.shared === 'yes' ? 'SHARED' : currentUser?._id,
      bot: botId,
    };
    if (String(modalInfo.action).toUpperCase() === 'CREATE')
      secureVariableCreate({
        ...payload,
      });
    else
      secureVariableUpdate({
        ...payload,
        _id: stateVaultId,
        oldName: modalInfo.form.name,
      });
  };

  useEffect(() => {
    if (!currentLoading && !currentTenant?.userPermissions?.includes(UserPermissions.BotVaultManager)) {
      history.push('/admin/bot');
      notificationError(t(`codeResponse.403`));
    }

    if (!vaultLoading) {
      const { success, data: passwordVault } = vaultData?.passwordVaultFindAll;
      if (success) {
        setStateVaultId(passwordVault?._id);
        setStateVariables(passwordVault?.variables ?? []);
      }
    }
  }, [history, currentTenant, currentLoading, t, vaultData, vaultLoading]);

  return (
    <>
      <PageHeader ghost title={t('botVault.management')} />
      <Main darkMode={darkMode}>
        <BreadcrumbWrapperStyle darkMode={darkMode}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <NavLink to="/admin/">
                <HomeOutlined />
              </NavLink>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <NavLink to="/admin/bot">
                <span>{t('labels.bussiness.rpa.bots.title')}</span>
              </NavLink>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <NavLink to="#">{t('botVault.management')}</NavLink>
            </Breadcrumb.Item>
          </Breadcrumb>
        </BreadcrumbWrapperStyle>
        <Row gutter={15}>
          <Col xs={24}>
            <Cards
              isbutton={
                <div key="6" className="page-header-actions">
                  <Button
                    className="btn-signin"
                    size="small"
                    key="2"
                    type="primary"
                    onClick={() => handleModalStatus(true)}
                  >
                    <FeatherIcon icon="plus" size={14} />
                    {t('botVault.create')}
                  </Button>
                </div>
              }
            >
              <PasswordVaultList
                variablesData={stateVariables}
                isTenantVault={false}
                loading={vaultLoading}
                handleSecureVariableUpdate={handleSecureVariableUpdate}
                handleSecureVariableDestroy={handleSecureVariableDestroy}
              />

              <Modal
                type="primary"
                title={modalInfo.action === 'CREATE' ? t('botVault.create') : t('botVault.edit')}
                visible={showModal}
                style={{ top: 30 }}
                width="60%"
                footer={null}
                onCancel={() => handleModalStatus()}
              >
                <PasswordVaultForm
                  action={modalInfo.action}
                  initialForm={modalInfo.form}
                  isTenantVault={false}
                  loading={isSubmitted}
                  handleSubmit={handleSubmit}
                  sharedVariablesList={stateVariables?.filter(x => x.user === 'SHARED')}
                />
              </Modal>
            </Cards>
          </Col>
        </Row>
      </Main>
    </>
  );
};

export default BotVaultManagement;
